/**
 * ActivityFeed — icon-dot list for admin recent activity.
 *
 * Each item has a colored square icon dot (type-driven), title, meta, and time.
 * Handles arbitrary item count; last item removes bottom border.
 */
import type { ReactNode } from "react";
import { cn } from "@/shared/lib/utils";
import { useLocale } from "@/shared/hooks/useLocale";

export type ActivityType = "published" | "submitted" | "approved" | "registered" | "rejected";

export interface ActivityItem {
  type: ActivityType;
  title: string;
  meta: string;
  time: string;
}

interface ActivityFeedProps {
  items: ActivityItem[];
  className?: string;
}

const TYPE_STYLES: Record<ActivityType, { bg: string; color: string }> = {
  published:  { bg: "var(--published-bg)",  color: "var(--success-text, #2D5E35)" },
  submitted:  { bg: "var(--info-bg, rgba(58,107,196,0.1))", color: "var(--info-text, #3A6BC4)" },
  approved:   { bg: "var(--success-bg, rgba(45,94,53,0.1))", color: "var(--success-text, #2D5E35)" },
  registered: { bg: "var(--terracotta-tint)", color: "var(--gold-text)" },
  rejected:   { bg: "var(--error-bg, rgba(184,76,58,0.1))", color: "var(--error-text, #B84C3A)" },
};

const TYPE_ICONS: Record<ActivityType, ReactNode> = {
  published:  <polyline points="20 6 9 17 4 12" />,
  submitted:  <><line x1="12" y1="19" x2="12" y2="5" /><polyline points="5 12 12 5 19 12" /></>,
  approved:   <polyline points="20 6 9 17 4 12" />,
  registered: <><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><line x1="19" y1="8" x2="19" y2="14" /><line x1="22" y1="11" x2="16" y2="11" /></>,
  rejected:   <><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>,
};

export function ActivityFeed({ items, className }: ActivityFeedProps) {
  const { fmtDigits } = useLocale();
  return (
    <div className={cn("flex flex-col", className)}>
      {items.map((item, idx) => {
        const { bg, color } = TYPE_STYLES[item.type];
        const icon = TYPE_ICONS[item.type];
        return (
          <div
            key={idx}
            className="flex items-start gap-2.5 py-2.5 last:pb-0 first:pt-0"
            style={idx < items.length - 1 ? { borderBottom: "1px solid var(--border-light)" } : undefined}
          >
            <div
              className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 mt-0.5"
              style={{ background: bg }}
            >
              <svg
                width="11" height="11" viewBox="0 0 24 24"
                fill="none" stroke={color} strokeWidth="2.5"
                strokeLinecap="round" strokeLinejoin="round"
              >
                {icon}
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[12.5px] font-semibold leading-snug" style={{ color: "var(--text-primary)" }}>
                {item.title}
              </p>
              <p className="text-[11px] mt-0.5" style={{ color: "var(--text-tertiary)" }}>
                {item.meta}
              </p>
            </div>
            <span className="text-[10.5px] shrink-0 pt-0.5" style={{ color: "var(--text-tertiary)" }}>
              {fmtDigits(item.time)}
            </span>
          </div>
        );
      })}
    </div>
  );
}
